import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, AlertTriangle, AlertCircle, Users2, HelpCircle, ChevronDown,
  Loader2, Play, Clock, CheckCircle2, FileDown, Check, Search, ArrowRight, X, Sparkles, RotateCw
} from 'lucide-react';
import avvikelserapportLogo from '../../assets/avvikelserapport-logo.png';
import { UNCHECKED_REASON_META, groupUncheckedResults } from '../../utils/subletAuditReasons';
import { isUncheckedVerdict } from '../../utils/auditSummary';
import {
  applyManualVerdicts,
  manualVerdictKey,
  manualVerdictsFromOverrides,
  reconcileManualVerdicts,
  splitManualVerdicts,
} from '../../utils/auditManualVerdict';

/* ── Toast notification ────────────────────────────── */
function Toast({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-[fadeSlideIn_0.3s_ease-out]">
      <div className="bg-[#222222] text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 text-sm">
        <CheckCircle2 className="w-4 h-4 text-[#4ADE80] flex-shrink-0" />
        {message}
        <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

/* ── Scroll-fade table wrapper ─────────────────────── */
function ScrollFadeTable({ children }) {
  const scrollRef = useRef(null);
  const [atBottom, setAtBottom] = useState(false);
  const [needsFade, setNeedsFade] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setNeedsFade(el.scrollHeight > el.clientHeight);
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll, children]);

  return (
    <div className="relative">
      <div ref={scrollRef} onScroll={checkScroll} className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 420px)', minHeight: 200 }}>
        {children}
      </div>
      {needsFade && !atBottom && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent" />
      )}
    </div>
  );
}

/* ── Accordion row ─────────────────────────────────── */
const variantStyles = {
  danger: { icon: 'text-[#DC2626]', badge: 'bg-[#FEE2E2] text-[#991B1B]' },
  warning: { icon: 'text-[#D97706]', badge: 'bg-[#FEF3C7] text-[#92400E]' },
  success: { icon: 'text-[#16A34A]', badge: 'bg-[#DCFCE7] text-[#166534]' },
  default: { icon: 'text-[#62B8E9]', badge: 'bg-[#E2EAF0] text-[#55585A]' },
};

function AccordionRow({ icon: Icon, label, count, description, children, defaultOpen = false, variant = 'default' }) {
  const styles = variantStyles[variant] || variantStyles.default;
  const [open, setOpen] = useState(defaultOpen);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [open, children]);

  return (
    <div className="border-t border-[#E2EAF0]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3.5 px-1 hover:bg-[#F9FCFF] transition-colors duration-150 rounded"
      >
        <div className="flex items-center gap-2.5">
          <Icon className={`w-4 h-4 ${styles.icon}`} />
          <span className="font-medium text-sm text-[#222222]">{label}</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styles.badge}`}>
            {count}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#55585A] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-250 ease-in-out"
        style={{ maxHeight: open ? contentHeight + 16 : 0, opacity: open ? 1 : 0 }}
      >
        <div ref={contentRef} className="pb-4 px-1">
          {description && <p className="text-xs text-[#55585A] mb-3">{description}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── PersonChip ────────────────────────────────────── */
function PersonChip({ person, variant = 'default' }) {
  const colors = {
    danger: 'text-[#991B1B] bg-[#FEE2E2] border-[#FECACA]',
    warning: 'text-[#92400E] bg-[#FEF3C7] border-[#FDE68A]',
    default: 'text-[#222222] bg-[#62B8E9]/10 border-[#62B8E9]/20',
    success: 'text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]',
  };
  return (
    <span className={`inline-flex items-center text-xs rounded-md px-2 py-0.5 font-medium border ${colors[variant] || colors.default}`}>
      {person.firstName} {person.lastName}{person.age != null ? `, ${person.age} år` : ''}
    </span>
  );
}

/* ── Searchable association picker ─────────────────── */
function AssociationPicker({ associations, selectedId, onChange }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return associations;
    const term = searchTerm.toLowerCase();
    return associations.filter((a) => (a.name || '').toLowerCase().includes(term));
  }, [associations, searchTerm]);

  const selectedName = associations.find((a) => a.id === selectedId)?.name || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative min-w-[280px]">
      <div
        className={`bg-[#F9FCFF] border rounded-lg flex items-center gap-2 px-3 py-2 cursor-pointer transition-all
          ${isOpen ? 'border-[#62B8E9] ring-2 ring-[#62B8E9]/40' : 'border-[#E2EAF0]'}`}
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
      >
        <Search className="w-3.5 h-3.5 text-[#55585A] flex-shrink-0" />
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök förening..."
            className="bg-transparent text-sm text-[#222222] outline-none flex-1 min-w-0"
          />
        ) : (
          <span className="text-sm text-[#222222] truncate flex-1">{selectedName}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-[#55585A] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2EAF0] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#55585A]">Inga föreningar hittades</div>
          ) : (
            filtered.map((a) => (
              <button
                key={a.id}
                onClick={() => { onChange(a.id); setIsOpen(false); setSearchTerm(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F9FCFF] transition-colors
                  ${a.id === selectedId ? 'bg-[#62B8E9]/10 text-[#222222] font-medium' : 'text-[#55585A]'}`}
              >
                {a.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ── Apply overrides helper ────────────────────────── */
function applyOverrides(apartmentResults, localOverrides) {
  return (apartmentResults || []).flatMap((r) => {
    const updatedFlagged = [];
    const updatedInneboende = [];
    let hasChanges = false;

    for (const f of (r.flaggedResidents || [])) {
      const key = `${r.apartmentNumber}|${f.firstName}|${f.lastName}`;
      const ov = localOverrides[key];
      if (ov) {
        hasChanges = true;
        if (ov === 'INNEBOENDE') updatedInneboende.push(f);
        if (ov === 'AVVIKELSE') updatedFlagged.push(f);
      } else {
        updatedFlagged.push(f);
      }
    }

    for (const f of (r.inneboendeResidents || [])) {
      const key = `${r.apartmentNumber}|${f.firstName}|${f.lastName}`;
      const ov = localOverrides[key];
      if (ov) {
        hasChanges = true;
        if (ov === 'AVVIKELSE') updatedFlagged.push(f);
        if (ov === 'INNEBOENDE') updatedInneboende.push(f);
      } else {
        updatedInneboende.push(f);
      }
    }

    if (!hasChanges) return [r];

    if (updatedFlagged.length > 0 && updatedInneboende.length > 0) {
      return [
        { ...r, verdict: 'AVVIKELSE', flaggedResidents: updatedFlagged, inneboendeResidents: [] },
        { ...r, verdict: 'INNEBOENDE', flaggedResidents: [], inneboendeResidents: updatedInneboende },
      ];
    }

    let newVerdict = r.verdict;
    if (r.verdict === 'AVVIKELSE' || r.verdict === 'INNEBOENDE') {
      if (updatedFlagged.length > 0) newVerdict = 'AVVIKELSE';
      else if (updatedInneboende.length > 0) newVerdict = 'INNEBOENDE';
      else newVerdict = 'GODKAND';
    }

    return [{
      ...r,
      verdict: newVerdict,
      flaggedResidents: updatedFlagged,
      inneboendeResidents: updatedInneboende,
    }];
  });
}

/* Person-flyttar + manuella bedömningar av rader som inte gick att kontrollera.
   En sparad körning kan redan bära bedömningar — de delas ut först så att de
   går att ångra, och läggs sedan på igen tillsammans med sessionens ändringar. */
function buildEffectiveResults(apartmentResults, localOverrides, localManual) {
  const { base, manual } = splitManualVerdicts(apartmentResults);
  return applyManualVerdicts(applyOverrides(base, localOverrides), { ...manual, ...localManual });
}

/* Märkning + ångra för rader som bedömts för hand */
function ManualVerdictNote({ row, onUndo }) {
  if (!row.manualVerdict) return null;
  return (
    <span className="inline-flex items-center gap-2 text-[11px] text-[#55585A]">
      <span className="px-1.5 py-0.5 rounded bg-[#EEF2F6] font-medium">Bedömd för hand</span>
      {onUndo && (
        <button
          type="button"
          onClick={() => onUndo(row)}
          className="underline underline-offset-2 hover:text-[#222222]"
        >
          Ångra
        </button>
      )}
    </span>
  );
}

/* ── Report View ───────────────────────────────────── */
function ReportView({ report, isNew, onOverride, localOverrides, onManualVerdict, localManual }) {
  const [selectedAvvikelser, setSelectedAvvikelser] = useState({});
  const [selectedInneboende, setSelectedInneboende] = useState({});
  const [selectedUnchecked, setSelectedUnchecked] = useState({});

  const apartmentResults = report?.apartmentResults || [];
  const summary = report?.summary;

  const effectiveResults = useMemo(
    () => buildEffectiveResults(apartmentResults, localOverrides, localManual),
    [apartmentResults, localOverrides, localManual]
  );

  if (!summary) return null;

  const sortByAptAndAddress = (a, b) => {
    const aptA = parseInt(a.apartmentNumber) || 0;
    const aptB = parseInt(b.apartmentNumber) || 0;
    if (aptA !== aptB) return aptA - aptB;
    return (a.address || '').localeCompare(b.address || '', 'sv');
  };

  const avvikelser = effectiveResults.filter((r) => r.verdict === 'AVVIKELSE').sort(sortByAptAndAddress);
  const inneboende = effectiveResults.filter((r) => r.verdict === 'INNEBOENDE').sort(sortByAptAndAddress);
  const godkanda = effectiveResults.filter((r) => r.verdict === 'GODKAND').sort(sortByAptAndAddress);
  // Samma definition som rapporten — förut räknade den bara NO_DATA, vyn båda.
  const unchecked = effectiveResults.filter((r) => isUncheckedVerdict(r.verdict)).sort(sortByAptAndAddress);
  const uncheckedByReason = groupUncheckedResults(unchecked);

  // Räkna unika lägenheter per verdict (inte per ägare/medlem)
  const countUniqueApartments = (results) => {
    const seen = new Set();
    results.forEach((r) => seen.add(`${r.apartmentNumber}|||${r.address}`));
    return seen.size;
  };
  const uniqueAvvikelserCount = countUniqueApartments(avvikelser);
  const uniqueInneboendeCount = countUniqueApartments(inneboende);
  const uniqueGodkandaCount = countUniqueApartments(godkanda);

  const toggleAvvikelse = (aptNumber, person) => {
    const key = `${aptNumber}|${person.firstName}|${person.lastName}`;
    setSelectedAvvikelser((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleInneboende = (aptNumber, person) => {
    const key = `${aptNumber}|${person.firstName}|${person.lastName}`;
    setSelectedInneboende((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getSelectedFromSection = (section, selectedMap) => {
    const items = [];
    section.forEach((r) => {
      const residents = r.verdict === 'AVVIKELSE' ? r.flaggedResidents : r.inneboendeResidents || [];
      residents.forEach((f) => {
        const key = `${r.apartmentNumber}|${f.firstName}|${f.lastName}`;
        if (selectedMap[key]) {
          items.push({ apartmentNumber: r.apartmentNumber, residentFirstName: f.firstName, residentLastName: f.lastName });
        }
      });
    });
    return items;
  };

  const handleMoveAvvikelser = (targetVerdict) => {
    const items = getSelectedFromSection(avvikelser, selectedAvvikelser);
    if (items.length > 0 && onOverride) {
      onOverride(items, targetVerdict);
      setSelectedAvvikelser({});
    }
  };

  const handleMoveInneboende = (targetVerdict) => {
    const items = getSelectedFromSection(inneboende, selectedInneboende);
    if (items.length > 0 && onOverride) {
      onOverride(items, targetVerdict);
      setSelectedInneboende({});
    }
  };

  // Rader som inte gick att kontrollera har ingen folkbokförd person att bocka
  // i — där väljs hela raden (medlemmen och lägenheten).
  const toggleUnchecked = (row) => {
    const key = manualVerdictKey(row);
    setSelectedUnchecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const handleMoveUnchecked = (targetVerdict) => {
    const rows = unchecked.filter((r) => selectedUnchecked[manualVerdictKey(r)]);
    if (rows.length > 0 && onManualVerdict) {
      onManualVerdict(rows, targetVerdict);
      setSelectedUnchecked({});
    }
  };
  const undoManualVerdict = onManualVerdict ? (row) => onManualVerdict([row], 'RESET') : undefined;
  const hasSelectedUnchecked = Object.values(selectedUnchecked).some(Boolean);

  const hasSelectedAvvikelser = Object.values(selectedAvvikelser).some(Boolean);
  const hasSelectedInneboende = Object.values(selectedInneboende).some(Boolean);

  const uniqueTotal = countUniqueApartments(effectiveResults);
  const uniqueUnchecked = countUniqueApartments(unchecked);
  const totalChecked = uniqueTotal - uniqueUnchecked;
  const avvikelsePercent = totalChecked > 0 ? Math.round((uniqueAvvikelserCount / totalChecked) * 100 * 10) / 10 : 0;
  const dateStr = report.createdAt ? new Date(report.createdAt).toLocaleDateString('sv-SE') : '';

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border border-[#E2EAF0] overflow-hidden transition-all duration-300 ${isNew ? 'animate-[fadeSlideIn_0.4s_ease-out]' : ''}`}
    >
      {/* Summary banner */}
      <div className="px-6 py-5 border-b border-[#E2EAF0]">
        <div className="flex flex-wrap gap-4 items-center">
          {uniqueAvvikelserCount > 0 && (
            <div className="flex items-center gap-2 bg-[#FEE2E2] text-[#991B1B] px-4 py-2 rounded-xl font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" />
              {uniqueAvvikelserCount} {uniqueAvvikelserCount === 1 ? 'avvikelse' : 'avvikelser'}
            </div>
          )}
          {uniqueInneboendeCount > 0 && (
            <div className="flex items-center gap-2 bg-[#FEF3C7] text-[#92400E] px-4 py-2 rounded-xl font-semibold text-sm">
              <Users2 className="w-4 h-4" />
              {uniqueInneboendeCount} inneboende
            </div>
          )}
          <div className="flex items-center gap-2 bg-[#DCFCE7] text-[#166534] px-4 py-2 rounded-xl font-semibold text-sm">
            <CheckCircle2 className="w-4 h-4" />
            {uniqueGodkandaCount} godkända
          </div>
        </div>
        {totalChecked > 0 && (
          <p className="text-xs text-[#55585A] mt-3">
            Avvikelser utgör {avvikelsePercent}% av kontrollerade lägenheter
            {dateStr && ` · Kontrollerad ${dateStr}`}
          </p>
        )}
      </div>

      {/* Card body */}
      <div className="px-6 py-5">
        {/* Avvikelser (röd) */}
        {avvikelser.length > 0 && (
          <AccordionRow
            icon={AlertTriangle}
            label="Avvikelser"
            count={uniqueAvvikelserCount}
            defaultOpen={true}
            variant="danger"
            description="Medlemmen är INTE folkbokförd men andra personer ÄR det, utan aktiv uthyrning via Hyria."
          >
            <ScrollFadeTable>
              <table className="w-full text-sm -mx-1">
                <thead className="sticky top-0 bg-[#FEF2F2] shadow-[0_1px_0_0_#FECACA] z-10">
                  <tr className="text-left text-xs font-semibold text-[#55585A] uppercase tracking-wider">
                    <th className="px-1 py-3">Lägenhet</th>
                    <th className="px-3 py-3">Adress</th>
                    <th className="px-3 py-3">Ägare</th>
                    <th className="px-3 py-3">Folkbokförda</th>
                  </tr>
                </thead>
                <tbody>
                  {avvikelser.map((r, aptIdx) => {
                    const prev = aptIdx > 0 ? avvikelser[aptIdx - 1] : null;
                    const sameGroup = prev && prev.apartmentNumber === r.apartmentNumber && prev.address === r.address;
                    const memberName = r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`;
                    return (
                      <tr key={aptIdx} className={`hover:bg-[#FEF2F2] transition-colors duration-100 ${sameGroup ? 'border-t border-[#E2EAF0]/50' : aptIdx > 0 ? 'border-t-2 border-[#E2EAF0]' : ''}`}>
                        <td className="px-1 py-3.5 whitespace-nowrap">
                          {!sameGroup && <span className="font-mono font-semibold text-[#222222]">{r.apartmentNumber || '–'}</span>}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A] text-xs">
                          {!sameGroup && r.address}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{memberName}</span>
                            {r.matchedApplication && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full"
                                title="Aktiv Hyria-uthyrning är registrerad för denna lägenhet — rapporteras detta som avvikelse är det troligen en bugg i matchningen"
                              >
                                ⚠ Aktiv uthyrning finns
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex flex-wrap gap-1.5">
                            {r.flaggedResidents.map((f, j) => {
                              const key = `${r.apartmentNumber}|${f.firstName}|${f.lastName}`;
                              return (
                                <label key={j} className="inline-flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!selectedAvvikelser[key]}
                                    onChange={() => toggleAvvikelse(r.apartmentNumber, f)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#62B8E9] focus:ring-[#62B8E9]"
                                  />
                                  <PersonChip person={f} variant="danger" />
                                </label>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollFadeTable>
            {hasSelectedAvvikelser && (
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => handleMoveAvvikelser('INNEBOENDE')}
                  className="flex items-center gap-2 bg-[#92400E] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#78350F] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <ArrowRight className="w-4 h-4" />
                  Flytta till Inneboende
                </button>
                <button
                  onClick={() => handleMoveAvvikelser('GODKAND')}
                  className="flex items-center gap-2 bg-[#166534] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#15803D] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <Check className="w-4 h-4" />
                  Flytta till Godkänd
                </button>
              </div>
            )}
          </AccordionRow>
        )}

        {/* Inneboende (gul) */}
        {inneboende.length > 0 && (
          <AccordionRow
            icon={Users2}
            label="Inneboende"
            count={uniqueInneboendeCount}
            variant="warning"
            description="Medlemmen ÄR folkbokförd, men andra vuxna bor också på adressen."
          >
            <ScrollFadeTable>
              <table className="w-full text-sm -mx-1">
                <thead className="sticky top-0 bg-[#FFFBEB] shadow-[0_1px_0_0_#FDE68A] z-10">
                  <tr className="text-left text-xs font-semibold text-[#55585A] uppercase tracking-wider">
                    <th className="px-1 py-3">Lägenhet</th>
                    <th className="px-3 py-3">Adress</th>
                    <th className="px-3 py-3">Folkbokförd medlem</th>
                    <th className="px-3 py-3">Inneboende</th>
                  </tr>
                </thead>
                <tbody>
                  {inneboende.map((r, i) => {
                    const prev = i > 0 ? inneboende[i - 1] : null;
                    const sameGroup = prev && prev.apartmentNumber === r.apartmentNumber && prev.address === r.address;
                    const memberName = r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`;
                    const inneboendeList = r.inneboendeResidents || [];
                    return (
                      <tr key={i} className={`hover:bg-[#FFFBEB] transition-colors duration-100 ${sameGroup ? 'border-t border-[#E2EAF0]/50' : i > 0 ? 'border-t-2 border-[#E2EAF0]' : ''}`}>
                        <td className="px-1 py-3.5 whitespace-nowrap">
                          {!sameGroup && <span className="font-mono font-semibold text-[#222222]">{r.apartmentNumber || '–'}</span>}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A] text-xs">
                          {!sameGroup && r.address}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A]">{memberName}</td>
                        <td className="px-3 py-3.5">
                          <div className="flex flex-wrap gap-1.5">
                            {r.manualVerdict && (
                              <span className="inline-flex flex-wrap items-center gap-2 text-xs text-[#55585A]">
                                Ingen folkbokförd hittad
                                <ManualVerdictNote row={r} onUndo={undoManualVerdict} />
                              </span>
                            )}
                            {inneboendeList.map((f, j) => {
                              const key = `${r.apartmentNumber}|${f.firstName}|${f.lastName}`;
                              return (
                                <label key={j} className="inline-flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!selectedInneboende[key]}
                                    onChange={() => toggleInneboende(r.apartmentNumber, f)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#D97706] focus:ring-[#D97706]"
                                  />
                                  <PersonChip person={f} variant="warning" />
                                </label>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollFadeTable>
            {hasSelectedInneboende && (
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => handleMoveInneboende('AVVIKELSE')}
                  className="flex items-center gap-2 bg-[#991B1B] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#7F1D1D] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Flytta till Avvikelse
                </button>
                <button
                  onClick={() => handleMoveInneboende('GODKAND')}
                  className="flex items-center gap-2 bg-[#166534] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#15803D] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <Check className="w-4 h-4" />
                  Flytta till Godkänd
                </button>
              </div>
            )}
          </AccordionRow>
        )}

        {/* Godkända (grön) */}
        {godkanda.length > 0 && (
          <AccordionRow
            icon={ShieldCheck}
            label="Godkända lägenheter"
            count={uniqueGodkandaCount}
            variant="success"
            description="Medlemmen är folkbokförd och inga avvikelser hittades."
          >
            <ScrollFadeTable>
              <table className="w-full text-sm -mx-1">
                <thead className="sticky top-0 bg-[#F0FDF4] shadow-[0_1px_0_0_#BBF7D0] z-10">
                  <tr className="text-left text-xs font-semibold text-[#55585A] uppercase tracking-wider">
                    <th className="px-1 py-3">Lägenhet</th>
                    <th className="px-3 py-3">Adress</th>
                    <th className="px-3 py-3">Medlem</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {godkanda.map((r, i) => {
                    const prev = i > 0 ? godkanda[i - 1] : null;
                    const sameGroup = prev && prev.apartmentNumber === r.apartmentNumber && prev.address === r.address;
                    const memberName = r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`;
                    return (
                      <tr key={i} className={`hover:bg-[#F0FDF4] transition-colors duration-100 ${sameGroup ? 'border-t border-[#E2EAF0]/50' : i > 0 ? 'border-t-2 border-[#E2EAF0]' : ''}`}>
                        <td className="px-1 py-3.5 whitespace-nowrap">
                          {!sameGroup && <span className="font-mono font-semibold text-[#222222]">{r.apartmentNumber || '–'}</span>}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A] text-xs">
                          {!sameGroup && r.address}
                        </td>
                        <td className="px-3 py-3.5 text-[#55585A]">{memberName}</td>
                        <td className="px-3 py-3.5">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#166534] bg-[#DCFCE7] px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Godkänd
                          </span>
                          <span className="ml-2"><ManualVerdictNote row={r} onUndo={undoManualVerdict} /></span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollFadeTable>
          </AccordionRow>
        )}

        {/* Ej kontrollerade — uppdelade per orsak, så föreningen ser vad de
            själva kan rätta till och vad som är ett tekniskt fel hos oss */}
        {unchecked.length > 0 && (
          <AccordionRow
            icon={HelpCircle}
            label="Kunde inte kontrolleras"
            count={uniqueUnchecked}
            description="Uppdelat per orsak — vissa kan föreningen åtgärda själv."
          >
            <div className="space-y-4">
              {Object.entries(uncheckedByReason)
                .filter(([, rows]) => rows.length > 0)
                .map(([reason, rows]) => {
                  const meta = UNCHECKED_REASON_META[reason];
                  return (
                    <div key={reason}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[#222222]">
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-[#626262]">
                          {rows.length} st
                        </span>
                        {meta.fixableByAssociation && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                            Åtgärdas av föreningen
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#626262] mt-0.5 mb-1">
                        {meta.description}
                      </p>
                      <div className="divide-y divide-[#E2EAF0]">
                        {rows.map((r, i) => {
                          const name = r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`;
                          const rowKey = manualVerdictKey(r);
                          return (
                            <div key={i} className="py-2 text-xs text-[#55585A]">
                              <div className="flex items-center justify-between">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!selectedUnchecked[rowKey]}
                                    onChange={() => toggleUnchecked(r)}
                                    aria-label={`Välj ${name}, ${r.address}`}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#55585A] focus:ring-[#55585A]"
                                  />
                                  <span>
                                    <span className="font-mono font-medium text-[#222222] mr-2">{r.apartmentNumber || '–'}</span>
                                    {r.address}
                                  </span>
                                </label>
                                <span>{name}</span>
                              </div>
                              {r.verdictReason && (
                                <p className="text-[10px] italic text-[#8B8E91] mt-0.5 ml-12">{r.verdictReason}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
            {hasSelectedUnchecked && (
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => handleMoveUnchecked('INNEBOENDE')}
                  className="flex items-center gap-2 bg-[#92400E] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#78350F] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <ArrowRight className="w-4 h-4" />
                  Flytta till Inneboende
                </button>
                <button
                  onClick={() => handleMoveUnchecked('GODKAND')}
                  className="flex items-center gap-2 bg-[#166534] text-white px-4 py-2 rounded-xl text-sm font-semibold
                             hover:bg-[#15803D] active:scale-[0.97] transition-all duration-150 shadow-sm"
                >
                  <Check className="w-4 h-4" />
                  Flytta till Godkänd
                </button>
              </div>
            )}
          </AccordionRow>
        )}
      </div>
    </div>
  );
}

/* ── AI Report Overlay ─────────────────────────────── */
/* ── Main component ────────────────────────────────── */
// Exporteras för test — vyn för en körnings resultat, utan datahämtning.
export { ReportView };

export default function SubletAudit() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [currentReport, setCurrentReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [associationId, setAssociationId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [reportIsNew, setReportIsNew] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localOverrides, setLocalOverrides] = useState({});
  // Manuella bedömningar av rader som inte gick att kontrollera (rad, inte person)
  const [localManual, setLocalManual] = useState({});
  const [toast, setToast] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');
  const [aiReportUrl, setAiReportUrl] = useState(null);

  // Hyria admin state
  const [userRole, setUserRole] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [selectedAssociationId, setSelectedAssociationId] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserRole(data.role);
          if (data.role === 'hyria_admin') {
            // Hämta alla föreningar
            const assocSnap = await getDocs(collection(db, 'associations'));
            const allAssocs = assocSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));
            setAssociations(allAssocs);
            if (allAssocs.length > 0) {
              setSelectedAssociationId(allAssocs[0].id);
              setAssociationId(allAssocs[0].id);
            }
          } else {
            setAssociationId(data.associationId);
          }
        }
      }
      setPageLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!associationId) return;
    setCurrentReport(null);
    setHistory([]);
    setLocalOverrides({});
    setLocalManual({});
    fetchHistory();
  }, [associationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = async () => {
    try {
      const q = query(collection(db, 'auditReports'), where('associationId', '==', associationId));
      const snap = await getDocs(q);
      const reports = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
        .slice(0, 10);
      setHistory(reports);

      const latestComplete = reports.find((r) => r.status === 'completed');
      if (latestComplete) setCurrentReport(latestComplete);
    } catch (err) {
      console.error('Error fetching audit history:', err);
    }
  };

  const handleAssociationChange = (newId) => {
    setSelectedAssociationId(newId);
    setAssociationId(newId);
  };

  const handleRunAudit = async () => {
    setRunning(true);
    setError(null);
    setProgress(0);
    setLocalOverrides({});
    setLocalManual({});

    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 90 ? prev : prev + Math.random() * 8));
    }, 2000);

    try {
      const runAudit = httpsCallable(functions, 'runSubletAudit', { timeout: 300000 });
      const result = await runAudit({ associationId });
      const { reportId, alreadyRunning } = result.data;

      if (alreadyRunning) {
        setError('En kontroll körs redan. Vänta tills den är klar.');
        setRunning(false);
        clearInterval(progressInterval);
        return;
      }

      const unsubReport = onSnapshot(doc(db, 'auditReports', reportId), (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (data.status === 'completed') {
          setProgress(100);
          clearInterval(progressInterval);
          setTimeout(() => {
            setCurrentReport({ id: snap.id, ...data });
            setReportIsNew(true);
            setRunning(false);
            fetchHistory();
            setTimeout(() => setReportIsNew(false), 500);
          }, 400);
          unsubReport();
        } else if (data.status === 'failed') {
          clearInterval(progressInterval);
          setError(data.errorMessage || 'Kontrollen misslyckades');
          setRunning(false);
          fetchHistory();
          unsubReport();
        }
      });

      setTimeout(() => {
        unsubReport();
        clearInterval(progressInterval);
        setRunning((prev) => {
          if (prev) { setError('Timeout — kontrollen tog för lång tid'); return false; }
          return prev;
        });
      }, 300000);
    } catch (err) {
      clearInterval(progressInterval);
      setError(err.message || 'Något gick fel');
      setRunning(false);
    }
  };

  const handleOverride = async (overrides, targetVerdict) => {
    const verdictLabels = { GODKAND: 'Godkänd', INNEBOENDE: 'Inneboende', AVVIKELSE: 'Avvikelse' };
    try {
      const saveOverride = httpsCallable(functions, 'saveAuditOverride');

      // Immediately update local state so rows move
      const newLocalOverrides = { ...localOverrides };
      for (const o of overrides) {
        const key = `${o.apartmentNumber}|${o.residentFirstName}|${o.residentLastName}`;
        newLocalOverrides[key] = targetVerdict;
      }
      setLocalOverrides(newLocalOverrides);

      // Show toast
      const count = overrides.length;
      setToast(`${count} ${count === 1 ? 'person' : 'personer'} flyttad till ${verdictLabels[targetVerdict] || targetVerdict}`);

      // Save to backend
      for (const o of overrides) {
        await saveOverride({
          associationId,
          apartmentNumber: o.apartmentNumber,
          residentFirstName: o.residentFirstName,
          residentLastName: o.residentLastName,
          overrideVerdict: targetVerdict,
          reason: `Manuellt flyttad till ${verdictLabels[targetVerdict] || targetVerdict} av admin`,
        });
      }
    } catch (err) {
      setError('Kunde inte spara override: ' + (err.message || ''));
    }
  };

  // Sparade bedömningar läses in när en rapport öppnas. Annars hoppar flyttade
  // rader tillbaka i vyn vid omladdning, tills nästa kontroll körs — och varje
  // kontroll kostar uppslag mot folkbokföringen.
  const currentReportId = currentReport?.id;
  const reportResults = currentReport?.apartmentResults;
  useEffect(() => {
    if (!associationId || !currentReportId) return undefined;
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'auditOverrides'), where('associationId', '==', associationId))
        );
        if (!alive) return;
        // Det sparade är sanningen — även mot bedömningar som är inbakade i en
        // äldre körning men sedan ångrats. Det som ändrats i sessionen vinner.
        const saved = reconcileManualVerdicts(
          reportResults,
          manualVerdictsFromOverrides(snap.docs.map((d) => d.data()))
        );
        setLocalManual((prev) => ({ ...saved, ...prev }));
      } catch (err) {
        console.warn('Kunde inte läsa sparade bedömningar:', err);
      }
    })();
    return () => { alive = false; };
  }, [associationId, currentReportId, reportResults]);

  // Flytta rader ur "Kunde inte kontrolleras" till Inneboende eller Godkänd,
  // eller tillbaka igen (RESET). Sparas så bedömningen finns kvar nästa körning.
  const handleManualVerdict = async (rows, targetVerdict) => {
    const verdictLabels = { GODKAND: 'Godkänd', INNEBOENDE: 'Inneboende', RESET: 'Kunde inte kontrolleras' };
    try {
      const saveOverride = httpsCallable(functions, 'saveAuditOverride');

      const next = { ...localManual };
      for (const r of rows) next[manualVerdictKey(r)] = targetVerdict;
      setLocalManual(next);

      const count = rows.length;
      setToast(`${count} ${count === 1 ? 'lägenhet' : 'lägenheter'} flyttad${count === 1 ? '' : 'e'} till ${verdictLabels[targetVerdict] || targetVerdict}`);

      for (const r of rows) {
        await saveOverride({
          kind: 'member',
          associationId,
          address: r.address,
          apartmentNumber: r.apartmentNumber || '',
          member: r.member,
          overrideVerdict: targetVerdict,
          reason: targetVerdict === 'RESET'
            ? null
            : `Manuellt bedömd som ${verdictLabels[targetVerdict]} av admin — kunde inte kontrolleras mot folkbokföringen`,
        });
      }
    } catch (err) {
      setError('Kunde inte spara bedömningen: ' + (err.message || ''));
    }
  };

  const handleExportPDF = async () => {
    if (!currentReport) return;
    const { default: jsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    const pdfDoc = new jsPDF();
    const assocName = currentReport.associationName || 'Förening';
    const dateStr = currentReport.createdAt ? new Date(currentReport.createdAt).toLocaleDateString('sv-SE') : '';

    // Apply overrides so PDF matches UI
    const effective = buildEffectiveResults(currentReport.apartmentResults || [], localOverrides, localManual);
    const sortByAptAndAddr = (a, b) => {
      const aptA = parseInt(a.apartmentNumber) || 0;
      const aptB = parseInt(b.apartmentNumber) || 0;
      if (aptA !== aptB) return aptA - aptB;
      return (a.address || '').localeCompare(b.address || '', 'sv');
    };
    const avvikelser = effective.filter((r) => r.verdict === 'AVVIKELSE').sort(sortByAptAndAddr);
    const inneboendeResults = effective.filter((r) => r.verdict === 'INNEBOENDE').sort(sortByAptAndAddr);
    const uncheckedResults = effective.filter((r) => isUncheckedVerdict(r.verdict)).sort(sortByAptAndAddr);

    const countUnique = (results) => {
      const seen = new Set();
      results.forEach((r) => seen.add(`${r.apartmentNumber}|||${r.address}`));
      return seen.size;
    };

    // Logo (upper right)
    try {
      pdfDoc.addImage(avvikelserapportLogo, 'PNG', 160, 5, 35, 50);
    } catch (_) { /* logo loading failed, skip */ }

    // Header
    pdfDoc.setFontSize(18);
    pdfDoc.text('Avvikelserapport', 14, 22);
    pdfDoc.setFontSize(11);
    pdfDoc.setTextColor(100);
    pdfDoc.text(`${assocName} — ${dateStr}`, 14, 30);
    pdfDoc.setTextColor(0);

    // Beräkna totalt granskade lägenheter
    const totalApartments = countUnique(effective);
    const uncheckedCount = countUnique(uncheckedResults);
    const totalChecked = totalApartments - uncheckedCount;
    const totalHousing = currentReport.summary?.totalHousing || totalApartments;

    // Sammanfattning — recalculated from effective results
    pdfDoc.autoTable({
      startY: 38,
      head: [['Kategori', 'Antal']],
      body: [
        ['Lägenheter i föreningen', String(totalHousing)],
        ['Avvikelser', String(countUnique(avvikelser))],
        ['Inneboende', String(countUnique(inneboendeResults))],
        ['Kunde ej kontrolleras', String(countUnique(uncheckedResults))],
      ],
      theme: 'grid',
      headStyles: { fillColor: [98, 184, 233] },
      styles: { fontSize: 9 },
    });

    // Avvikelser
    if (avvikelser.length > 0) {
      let avvY = pdfDoc.lastAutoTable.finalY + 14;
      pdfDoc.setFontSize(13);
      pdfDoc.setTextColor(0);
      pdfDoc.text('Avvikelser', 14, avvY);
      avvY += 5;
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100);
      pdfDoc.text(`Avvikelser hittades i ${countUnique(avvikelser)} av ${totalHousing} lägenheter.`, 14, avvY);
      avvY += 4;
      pdfDoc.text('Ägaren saknar folkbokföring — andra personer är folkbokförda utan registrerad uthyrning.', 14, avvY);
      pdfDoc.setTextColor(0);
      pdfDoc.autoTable({
        startY: avvY + 4,
        head: [['Lgh', 'Adress', 'Ägare', 'Folkbokförda']],
        body: avvikelser.map((r) => [
          r.apartmentNumber || '–',
          r.address || '',
          r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`,
          (r.flaggedResidents || []).map((f) => `${f.firstName} ${f.lastName}${f.age != null ? ` (${f.age})` : ''}`).join(', '),
        ]),
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 8 },
        columnStyles: { 3: { cellWidth: 60 } },
      });
    }

    // Inneboende
    if (inneboendeResults.length > 0) {
      let innY = pdfDoc.lastAutoTable.finalY + 14;
      pdfDoc.setFontSize(13);
      pdfDoc.setTextColor(0);
      pdfDoc.text('Inneboende', 14, innY);
      innY += 5;
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100);
      pdfDoc.text(`Inneboende hittades i ${countUnique(inneboendeResults)} av ${totalHousing} lägenheter.`, 14, innY);
      innY += 4;
      pdfDoc.text('Ägaren är folkbokförd — ytterligare vuxna personer är registrerade utan registrerad uthyrning.', 14, innY);
      pdfDoc.setTextColor(0);
      pdfDoc.autoTable({
        startY: innY + 4,
        head: [['Lgh', 'Adress', 'Medlem', 'Inneboende']],
        body: inneboendeResults.map((r) => [
          r.apartmentNumber || '–',
          r.address || '',
          r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`,
          r.manualVerdict
            ? 'Ingen folkbokförd hittad – bedömd för hand'
            : (r.inneboendeResidents || []).map((f) => `${f.firstName} ${f.lastName}${f.age != null ? ` (${f.age})` : ''}`).join(', '),
        ]),
        theme: 'grid',
        headStyles: { fillColor: [217, 119, 6] },
        styles: { fontSize: 8 },
        columnStyles: { 3: { cellWidth: 60 } },
      });
    }

    // Kunde inte kontrolleras — en tabell per orsak, så föreningen ser vad de
    // själva kan komplettera och vad som är ett tekniskt fel hos oss
    if (uncheckedResults.length > 0) {
      let unchY = pdfDoc.lastAutoTable.finalY + 14;
      pdfDoc.setFontSize(13);
      pdfDoc.setTextColor(0);
      pdfDoc.text('Kunde inte kontrolleras', 14, unchY);

      const groupedUnchecked = groupUncheckedResults(uncheckedResults);

      Object.entries(groupedUnchecked)
        .filter(([, rows]) => rows.length > 0)
        .forEach(([reason, rows]) => {
          const meta = UNCHECKED_REASON_META[reason];
          let sectionY = pdfDoc.lastAutoTable.finalY === unchY
            ? unchY + 8
            : pdfDoc.lastAutoTable.finalY + 10;
          if (sectionY < unchY + 8) sectionY = unchY + 8;

          pdfDoc.setFontSize(10);
          pdfDoc.setTextColor(0);
          pdfDoc.text(`${meta.label} (${rows.length})`, 14, sectionY);
          sectionY += 5;
          pdfDoc.setFontSize(8);
          pdfDoc.setTextColor(100);
          pdfDoc.text(pdfDoc.splitTextToSize(meta.description, 180), 14, sectionY);
          sectionY += 4 * pdfDoc.splitTextToSize(meta.description, 180).length;
          pdfDoc.setTextColor(0);

          pdfDoc.autoTable({
            startY: sectionY + 2,
            head: [['Lgh', 'Adress', 'Medlem', 'Anledning']],
            body: rows.map((r) => [
              r.apartmentNumber || '–',
              r.address || '',
              r.member.legalEntity || `${r.member.firstName} ${r.member.lastName}`,
              r.verdictReason || meta.label,
            ]),
            theme: 'grid',
            headStyles: { fillColor: [156, 163, 175] },
            styles: { fontSize: 8 },
            columnStyles: { 3: { cellWidth: 60 } },
          });
        });
    }

    // Filnamn
    const safeName = assocName.replace(/[^a-zåäö0-9]/gi, '_').toLowerCase();
    const safeDate = dateStr.replace(/\//g, '-');
    pdfDoc.save(`avvikelserapport_${safeName}_${safeDate}.pdf`);
  };

  const handleOpenAIReport = () => {
    if (aiReportUrl) window.open(aiReportUrl, '_blank');
  };

  const handleAIReport = async () => {
    if (!currentReport?.id) return;

    setAiLoading(true);
    setError(null);
    setAiProgress('Förbereder data...');

    // Stegvis progress-text
    const progressSteps = [
      { delay: 2000, text: 'Analyserar avvikelser med AI...' },
      { delay: 15000, text: 'Genererar sammanfattning...' },
      { delay: 25000, text: 'Skapar PDF...' },
    ];
    const timers = progressSteps.map(({ delay, text }) =>
      setTimeout(() => setAiProgress(text), delay)
    );

    try {
      const analyzeAuditResults = httpsCallable(functions, 'analyzeAuditResults', { timeout: 300000 });
      const result = await analyzeAuditResults({ auditReportId: currentReport.id });
      const { reportUrl } = result.data;

      setAiReportUrl(reportUrl || null);
      setAiProgress('');
    } catch (err) {
      console.error('AI report error:', err);
      setError(err.message || 'Kunde inte generera AI-rapport');
      setAiProgress('');
    } finally {
      timers.forEach(clearTimeout);
      setAiLoading(false);
    }
  };

  // Ladda befintlig AI-analys från Firestore när rapport byts
  useEffect(() => {
    if (!currentReport?.aiAnalysis) {
      setAiReportUrl(null);
      return;
    }
    const { status, reportUrl } = currentReport.aiAnalysis;
    if (status === 'completed' && reportUrl) {
      setAiReportUrl(reportUrl);
    } else {
      setAiReportUrl(null);
    }
  }, [currentReport?.id]);

  const handleSelectReport = async (reportId) => {
    try {
      const snap = await getDoc(doc(db, 'auditReports', reportId));
      if (snap.exists()) {
        setReportIsNew(true);
        setCurrentReport({ id: snap.id, ...snap.data() });
        setLocalOverrides({});
        setLocalManual({});
        setTimeout(() => setReportIsNew(false), 500);
      }
    } catch (err) {
      setError('Kunde inte ladda rapport');
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#62B8E9]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Toast */}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#222222] font-['Poppins']">{t('audit.title', 'Avvikelserapport')}</h1>
            <p className="text-[#55585A] text-sm mt-1">
              Kontrollera om det bor personer i era lägenheter som inte ska bo där.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-2xl border border-[#E2EAF0] px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm">
          {userRole === 'hyria_admin' && associations.length > 0 && (
            <AssociationPicker
              associations={associations}
              selectedId={selectedAssociationId}
              onChange={handleAssociationChange}
            />
          )}
          <div className="flex-1" />
          {!running && currentReport && (
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1.5 text-[#55585A] px-3 py-2 rounded-lg text-sm
                         hover:bg-[#F0F4F8] hover:text-[#222222] active:scale-[0.97] transition-all duration-150"
            >
              <FileDown className="w-3.5 h-3.5" /> Exportera PDF
            </button>
          )}
          {!running && currentReport && (currentReport.summary?.avvikelser > 0 || currentReport.summary?.inneboende > 0) && (
            <>
              {aiReportUrl && !aiLoading && (
                <button
                  onClick={handleOpenAIReport}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                             text-[#6D28D9] bg-[#F3EEFF] border border-[#DDD6FE] hover:bg-[#EDE9FE]
                             active:scale-[0.97] transition-all duration-150"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Öppna AI-rapport
                </button>
              )}
              <button
                onClick={handleAIReport}
                disabled={aiLoading}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                           active:scale-[0.97] transition-all duration-150
                           disabled:opacity-50 disabled:cursor-not-allowed
                           ${aiReportUrl && !aiLoading
                             ? 'text-[#55585A] hover:bg-[#F0F4F8] hover:text-[#222222]'
                             : 'text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:from-[#7C3AED] hover:to-[#5B21B6]'
                           }`}
              >
                {aiLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyserar...</>
                ) : aiReportUrl ? (
                  <><RotateCw className="w-3.5 h-3.5" /> Kör ny analys</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5" /> AI-rapport</>
                )}
              </button>
            </>
          )}
          <button
            onClick={handleRunAudit}
            disabled={running}
            className="flex items-center gap-2 bg-[#62B8E9] text-white px-4 py-2 rounded-lg font-medium text-sm
                       hover:bg-[#4da8db] active:scale-[0.97] transition-all duration-150
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {running ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('audit.running', 'Kontroll pågår...')}</>
            ) : (
              <><Play className="w-3.5 h-3.5" /> {t('audit.runAudit', 'Kör kontroll')}</>
            )}
          </button>
        </div>
      </div>

      {/* AI Progress */}
      {aiLoading && aiProgress && (
        <div className="flex items-center gap-2 px-4 py-2 mb-3 bg-[#F3EEFF] border border-[#DDD6FE] rounded-xl text-sm text-[#6D28D9] animate-[fadeSlideIn_0.3s_ease-out]">
          <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
          {aiProgress}
        </div>
      )}

      {/* AI Report ready */}
      {aiReportUrl && !aiLoading && (
        <div className="flex items-center gap-3 px-4 py-3 mb-3 bg-[#F3EEFF] border border-[#DDD6FE] rounded-xl text-sm animate-[fadeSlideIn_0.3s_ease-out]">
          <CheckCircle2 className="w-4 h-4 text-[#6D28D9] flex-shrink-0" />
          <span className="text-[#6D28D9]">AI-rapporten är klar.</span>
          <a
            href={aiReportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#6D28D9] font-medium underline underline-offset-2 hover:text-[#5B21B6] transition-colors"
          >
            Öppna rapport
          </a>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-white border border-[#E2EAF0] border-l-4 border-l-[#222222] rounded-xl p-4 mb-5 flex items-start gap-3 animate-[fadeSlideIn_0.3s_ease-out]">
          <AlertCircle className="w-4 h-4 text-[#55585A] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#222222]">{error}</p>
        </div>
      )}

      {/* Running state */}
      {running && (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2EAF0] p-6 mb-6 animate-[fadeSlideIn_0.3s_ease-out]">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F9FCFF] border border-[#E2EAF0] flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#62B8E9]" />
            </div>
            <div>
              <p className="font-semibold text-[#222222] font-['Poppins']">Kontroll pågår</p>
              <p className="text-sm text-[#55585A]">
                Hämtar folkbokföringsdata och jämför med medlemsregistret...
              </p>
            </div>
          </div>
          <div className="w-full h-1.5 bg-[#E2EAF0] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#62B8E9] rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Report */}
      {!running && currentReport && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#222222] font-['Poppins']">Resultat</h2>
            {currentReport.createdAt && (
              <span className="text-xs text-[#55585A] flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                {new Date(currentReport.createdAt).toLocaleString('sv-SE')}
                {currentReport.durationMs && ` (${Math.round(currentReport.durationMs / 1000)}s)`}
              </span>
            )}
          </div>
          <ReportView
            report={currentReport}
            isNew={reportIsNew}
            onOverride={handleOverride}
            localOverrides={localOverrides}
            onManualVerdict={handleManualVerdict}
            localManual={localManual}
          />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2EAF0] p-6">
          <h2 className="font-bold text-lg text-[#222222] font-['Poppins'] mb-4">
            {t('audit.previousReports', 'Tidigare kontroller')}
          </h2>
          <div className="space-y-2">
            {history.map((r) => {
              const isSelected = currentReport?.id === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => handleSelectReport(r.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left
                    transition-all duration-200 hover:shadow-md hover:border-[#62B8E9] hover:bg-[#F9FCFF]
                    ${isSelected
                      ? 'border-[#62B8E9] bg-[#F9FCFF] shadow-sm'
                      : 'border-[#E2EAF0] bg-white'
                    }`}
                >
                  <div className="flex items-center gap-3 text-sm">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-[#62B8E9]/10' : 'bg-[#F9FCFF]'
                    }`}>
                      {r.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-[#62B8E9]" />}
                      {r.status === 'completed' && <ShieldCheck className="w-4 h-4 text-[#62B8E9]" />}
                      {r.status === 'failed' && <AlertCircle className="w-4 h-4 text-[#55585A]" />}
                    </div>
                    <div>
                      <span className="font-medium text-[#222222]">
                        {new Date(r.createdAt).toLocaleDateString('sv-SE')}{' '}
                        {new Date(r.createdAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {r.summary && (
                        <span className="text-[#55585A] ml-2">{r.summary.totalHousing || r.summary.totalApartments} lägenheter</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.summary?.avvikelser > 0 && (
                      <span className="bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {r.summary.avvikelser} avvikelser
                      </span>
                    )}
                    {(r.summary?.inneboende || 0) > 0 && (
                      <span className="bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {r.summary.inneboende} inneboende
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!running && !currentReport && history.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2EAF0] border-dashed p-16 text-center animate-[fadeSlideIn_0.3s_ease-out]">
          <div className="w-16 h-16 rounded-full bg-[#F9FCFF] border border-[#E2EAF0] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="w-8 h-8 text-[#62B8E9]" />
          </div>
          <h3 className="font-semibold text-[#222222] font-['Poppins'] text-lg mb-2">Ingen kontroll har körts ännu</h3>
          <p className="text-sm text-[#55585A] max-w-sm mx-auto">
            Tryck på <span className="font-medium text-[#222222]">Kör kontroll</span> för att jämföra folkbokföringsdata mot ert medlemsregister.
          </p>
        </div>
      )}

    </div>
  );
}
